import { Line } from "@react-three/drei";
import * as THREE from "three";
import { RulerLineType, SingleLineType, WallType } from "../../types/floorplannerTypes";
import { SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";
import { createMiddleDragHandle } from "./createMiddleDragHandle";

const curvePoint = (
    start: THREE.Vector3,
    control: THREE.Vector3,
    end: THREE.Vector3,
    t: number,
) => {
    const invT = 1 - t;
    return new THREE.Vector3(
        invT * invT * start.x + 2 * invT * t * control.x + t * t * end.x,
        invT * invT * start.y + 2 * invT * t * control.y + t * t * end.y,
        start.z,
    );
};

const perpendicularVector = (vector: THREE.Vector3) => (
    new THREE.Vector3(
        -vector.y,
        vector.x,
        vector.z,
    )
);

const createArrowWings = (
    tip: THREE.Vector3,
    direction: THREE.Vector3,
    wingSize: number,
) => {
    const trailingVector = direction.clone().normalize().multiplyScalar(-wingSize);
    const sideVector = perpendicularVector(direction).normalize().multiplyScalar(wingSize);
    const base = tip.clone().add(trailingVector);

    return [
        [tip, base.clone().add(sideVector)],
        [tip, base.clone().sub(sideVector)],
    ];
};

export const CurveDragHandle = (props: {
    object: WallType | SingleLineType | RulerLineType;
    size: number;
    fillColor: string;
    color: string;
}) => {
    const { object, size, fillColor, color } = props;
    const noopRaycast = () => null;
    const z = 0.004;
    const strokeWidth = 1;
    const leftTip = new THREE.Vector3(-size * 0.36, -size * 0.26, z);
    const control = new THREE.Vector3(0, size * 0.62, z);
    const rightTip = new THREE.Vector3(size * 0.36, -size * 0.26, z);
    const curvePoints = Array.from({ length: 25 }, (_, index) => curvePoint(leftTip, control, rightTip, index / 24));
    const arrowWingSize = Math.min(0.016, size * 0.16);
    const leftArrowWings = createArrowWings(leftTip, leftTip.clone().sub(control), arrowWingSize);
    const rightArrowWings = createArrowWings(rightTip, rightTip.clone().sub(control), arrowWingSize);

    return (
        <group renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
            <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                <planeGeometry args={[size, size]} />
                <meshBasicMaterial color={fillColor} depthTest={false} depthWrite={false} />
            </mesh>
            <Line
                points={curvePoints}
                color={color}
                lineWidth={strokeWidth}
                raycast={noopRaycast}
                renderOrder={SELECTED_HANDLE_RENDER_ORDER + 1}
                depthTest={false}
                depthWrite={false}
            />
            {leftArrowWings.map((points, index) => (
                <Line
                    key={`left-arrow-wing-${index}`}
                    points={points}
                    color={color}
                    lineWidth={strokeWidth}
                    raycast={noopRaycast}
                    renderOrder={SELECTED_HANDLE_RENDER_ORDER + 1}
                    depthTest={false}
                    depthWrite={false}
                />
            ))}
            {rightArrowWings.map((points, index) => (
                <Line
                    key={`right-arrow-wing-${index}`}
                    points={points}
                    color={color}
                    lineWidth={strokeWidth}
                    raycast={noopRaycast}
                    renderOrder={SELECTED_HANDLE_RENDER_ORDER + 1}
                    depthTest={false}
                    depthWrite={false}
                />
            ))}
            <mesh raycast={noopRaycast} renderOrder={SELECTED_HANDLE_RENDER_ORDER + 2}>
                <primitive object={createMiddleDragHandle(object, size)} />
                <meshBasicMaterial color={color} transparent opacity={0.8} depthTest={false} depthWrite={false} />
            </mesh>
        </group>
    );
};
